import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Smartphone, Mail, Fingerprint, Lock, AlertCircle, RefreshCw, MapPin, X } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * SecurityStatusCard — live snapshot of the master admin's 2FA posture.
 * Only renders for role=admin users on the master admin email.
 * Pulls from /api/auth/admin-direct/security-status.
 */
const SecurityStatusCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [justRevoked, setJustRevoked] = useState(null);

  const load = useCallback(async () => {
    if (!apiClient.getToken()) { setLoading(false); setError('Not signed in'); return; }
    try {
      const d = await apiClient.request('/api/auth/admin-direct/security-status');
      setData(d);
      setError(null);
    } catch (e) {
      // Preserve the previous "silently hide for non-master admins" branch:
      // the backend returns HTTP 403 with detail "Master admin only" for non-master
      // admins, which apiClient.request() surfaces as the Error message verbatim.
      if (e && e.message === 'Master admin only') { setError('not_master'); }
      else { setError((e && e.message) || 'Error'); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async () => {
    if (!window.confirm('Sign-out all trusted devices and cancel any in-flight magic links? Your current session stays active.')) return;
    setRevoking(true);
    try {
      const d = await apiClient.request('/api/auth/admin-direct/security-status/revoke', { method: 'POST' });
      setJustRevoked(d);
      await load();
    } catch (e) {
      setError((e && e.message) || 'Revoke failed');
    } finally {
      setRevoking(false);
    }
  };

  // Don't render for non-master admins (403)
  if (error === 'not_master') return null;

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-4 animate-pulse" data-testid="security-status-loading" style={{ borderColor: '#E0E0E0' }}>
        <div className="h-4 w-40 bg-gray-200 rounded mb-3" />
        <div className="h-3 w-full bg-gray-100 rounded mb-2" />
        <div className="h-3 w-3/4 bg-gray-100 rounded" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-lg border p-4" data-testid="security-status-error" style={{ borderColor: '#E0E0E0' }}>
        <div className="flex items-center gap-2 text-sm text-red-700"><AlertCircle size={14} /> {error || 'Failed to load'}</div>
      </div>
    );
  }

  const { methods, trusted_devices, recent_logins, failed_attempts_24h } = data;
  const methodRow = (icon, label, active, extra = null) => (
    <div className="flex items-center gap-3 py-2 text-sm">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
        {icon}
      </div>
      <span className="flex-1 text-gray-700">{label}</span>
      <span className={`text-xs font-semibold ${active ? 'text-green-700' : 'text-gray-400'}`}>
        {extra !== null ? extra : (active ? 'ON' : 'OFF')}
      </span>
    </div>
  );

  const methodMeta = (
    (methods.sms_2fa ? 1 : 0) +
    (methods.email_2fa ? 1 : 0) +
    (methods.passkey_count > 0 ? 1 : 0) +
    (methods.trusted_device_count > 0 ? 1 : 0)
  );

  return (
    <div className="bg-white rounded-lg border" data-testid="security-status-card" style={{ borderColor: '#E0E0E0' }}>
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#F0F0F0' }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(245,131,31,0.1)' }}>
            <ShieldCheck size={16} style={{ color: '#F5831F' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Security Status</h3>
            <p className="text-xs text-gray-500">{methodMeta} active method{methodMeta === 1 ? '' : 's'} · {failed_attempts_24h} failed attempt{failed_attempts_24h === 1 ? '' : 's'} (24h)</p>
          </div>
        </div>
        <button onClick={load} className="text-gray-400 hover:text-gray-700 p-1 rounded" title="Refresh" data-testid="security-refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="px-4 py-2">
        {methodRow(<Smartphone size={14} />, 'SMS 2FA (Twilio)', methods.sms_2fa)}
        {methodRow(<Mail size={14} />, 'Email 2FA', methods.email_2fa)}
        {methodRow(<Fingerprint size={14} />, 'Fingerprint / Face ID', methods.passkey_count > 0, `${methods.passkey_count} device${methods.passkey_count === 1 ? '' : 's'}`)}
        {methodRow(<Lock size={14} />, 'Trusted devices (30d)', methods.trusted_device_count > 0, `${methods.trusted_device_count}`)}
      </div>

      {recent_logins && recent_logins.length > 0 && (
        <div className="px-4 py-3 border-t" style={{ borderColor: '#F0F0F0' }}>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Recent sign-ins</p>
          <div className="space-y-1.5">
            {recent_logins.slice(0, 3).map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-gray-600" data-testid={`recent-login-${i}`}>
                <MapPin size={10} className="text-gray-400 flex-shrink-0" />
                <span className="font-mono text-[11px] text-gray-500">{l.ip}</span>
                <span className="text-gray-400">·</span>
                <span className="flex-1 truncate">{l.reason || l.auth_method || 'password'}</span>
                <span className="text-gray-400 whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {justRevoked && (
        <div className="px-4 py-2 bg-green-50 border-t border-green-200 text-xs text-green-800 flex items-center justify-between">
          <span>Revoked {justRevoked.trusted_devices_removed} trusted device{justRevoked.trusted_devices_removed === 1 ? '' : 's'}, {justRevoked.magic_links_removed} magic link{justRevoked.magic_links_removed === 1 ? '' : 's'}.</span>
          <button onClick={() => setJustRevoked(null)} className="text-green-700 hover:text-green-900"><X size={12} /></button>
        </div>
      )}

      <div className="px-4 py-3 border-t flex items-center gap-2" style={{ borderColor: '#F0F0F0' }}>
        <a
          href="/api/auth/admin-direct/enroll"
          className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
          style={{ backgroundColor: 'rgba(245,131,31,0.1)', color: '#F5831F' }}
          data-testid="security-enroll-passkey"
        >
          Add Fingerprint / Face ID
        </a>
        <button
          onClick={revoke}
          disabled={revoking || (methods.trusted_device_count === 0 && methods.magic_link_active === 0)}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderColor: '#E0E0E0' }}
          data-testid="security-revoke-btn"
        >
          {revoking ? 'Revoking…' : 'Revoke other sessions'}
        </button>
      </div>
    </div>
  );
};

export default SecurityStatusCard;
